/** THE GAIN AUDIT — who held her down, and for how long (build 31).
 *
 * ── WHAT THIS EXISTS TO SETTLE ──────────────────────────────────────────────
 * Mid-call she goes silent. `gain 0.00`, nothing comes out, and the owner ends
 * the call. Two mechanisms can produce exactly that shape and NOTHING in the
 * lane could separate them:
 *
 *   · A DUCK THAT NEVER LIFTED. A barge fires and ramps the remote gain to zero.
 *     The resume rides `agent_speaking_start` — so if she never got to speak
 *     again, the resume never came, and the gain stayed where the barge put it.
 *   · A MUTE THAT NEVER CLEARED. The owner toggled mute six times inside the
 *     silent window. A mute that failed to lift looks identical from outside.
 *
 * Three read-only rounds could not decide between them, because the two facts
 * that would decide it were never in the same place: the GAIN is measured on the
 * device and the COMMANDS are issued in JavaScript. This module is that place.
 * It is a WITNESS, not a mechanism: it decides nothing, it changes nothing, and
 * removing it would restore byte-for-byte behaviour.
 *
 * ── THE NUMBER ALREADY EXISTED ──────────────────────────────────────────────
 * `remoteGain` has been emitted natively inside `playoutWitness` since build 20
 * (RexyMediaEngine.swift). JavaScript consumed it into the probe line and
 * NOWHERE ELSE — the box has never been told. Seventh instance in this lane of a
 * sender built with no receiver, and the first one where the missing receiver
 * cost a call nobody could read afterwards.
 *
 * That is also why this round needs NO Swift: the value is already on the wire
 * between the engine and the WebView. The web half reaches the phone on a
 * reload; nothing here waits on a Codemagic cycle.
 *
 * ── WHY THE PROBE IS NOT THE ANSWER ─────────────────────────────────────────
 * `mediaProbe` stops at MAX_PROBE_MS (25s) — it is a NEGOTIATION instrument. The
 * failure being chased happened minutes into a call, long after the probe had
 * ended, which is precisely why the readout could say nothing about it. The rows
 * this module produces are independent of probe lifetime and go to the box's
 * audit, where a record outlives the session that made it.
 *
 * ── EVERY CROSSING REPORTS ITS OWN FAILURE ──────────────────────────────────
 * A row that fails to leave is COUNTED here and carried on the next row that
 * lands (`undelivered`), so a gap in the trail is never silent. It is not
 * retried and not buffered: a diagnostic that fights for the wire during a
 * failure is a diagnostic that changes the failure.
 */

/** WHAT ASKED FOR THE CHANGE. The whole question is which of two mechanisms held
 *  the gain down, so a row without this field answers nothing.
 *  `none` = the gain moved with no command behind it, which is itself a finding. */
export type GainCause = 'duck' | 'resume' | 'mute' | 'unmute' | 'none';

/** WHY THIS ROW EXISTS — kept separate from WHAT CAUSED THE GAIN, because a
 *  periodic row observed 40s after a duck must say both things at once. */
export type GainRowKind = 'command' | 'settled' | 'echo' | 'transition' | 'periodic';

/** THE COMMAND'S OWN DELIVERY RESULT. A command that never reached its target is
 *  a third possibility nobody had excluded, so `unknown` is a real answer here
 *  and never a stand-in for `yes`. */
export type GainDelivery = 'yes' | 'no' | 'unknown';

export interface GainAuditRow {
  /** Monotonic within a call. A gap in the sequence is a row that never landed. */
  seq: number;
  /** THE DEVICE CLOCK THE ROW WAS COMPOSED ON, absolute epoch milliseconds.
   *
   *  The box stamps `iso`/`ts` when the row ARRIVES, and arrival is not
   *  occurrence: a hub block, a congested socket or a backgrounded app all move
   *  it. A reader following the audit chain into the spool joins on a timestamp,
   *  so without this the join is against arrival and quietly approximate — which
   *  is the difference between a receipt that points and one that nearly points.
   *
   *  IT IS THE CLOCK ALREADY TAKEN, not a new read: `row` is handed the same
   *  `now` that `sinceCommandMs` and `belowUnityMs` are computed from, so this
   *  costs nothing, stays injectable, and cannot disagree with the durations
   *  beside it. */
  deviceMs: number;
  kind: GainRowKind;
  cause: GainCause;
  delivered: GainDelivery;
  /** The turn the last gain command named. '' = no command has named one yet. */
  turnKey: string;
  /** The sampled remote gain. -1 is NO REMOTE TRACK, which is a different
   *  finding from a gain of zero and must never be read as one. */
  gain: number;
  /** Its change since the previous sample. 0 on a command row: nothing has been
   *  sampled yet at the moment a command is issued. */
  gainDelta: number;
  /** The playout counter beside the gain that explains it. `samplesDelta > 0`
   *  with `gain 0` is a duck: the clock runs, the frames are silent. Both zero
   *  is a different failure entirely, and one row now separates them. */
  samples: number;
  samplesDelta: number;
  /** How long since the command named in `cause`. This is what turns "it went
   *  silent" into "the duck at 13:48:29 was never lifted". */
  sinceCommandMs: number;
  /** How long the gain has sat below unity, unbroken. THE BAR: "it was at zero
   *  for 173 seconds and here is what put it there." */
  belowUnityMs: number;
  /** Rows that failed to leave since the last one that did. */
  undelivered: number;
  detail: string;
  /** WHICH MEDIA ENGINE PRODUCED THIS ROW (Gap 4). Optional because this module
   *  cannot know it — the auditor reaches no engine and is handed everything it
   *  reports. The one gated selection site sets it, which is the same place
   *  `gainAudited` and `probeBegin`'s label come from, so the three cannot drift
   *  into disagreeing about what ran where.
   *
   *  ON the row rather than beside it at the wire: two assertions in
   *  mediaProbe.test.mts pin the emit's argument, and a field spread in at the
   *  socket would be untyped, unreviewable and invisible to this interface — the
   *  row's own schema is where a field on the row belongs. */
  engine?: 'native' | 'browser';
  /** WHEN THE REMOTE TRACK WAS TAKEN, from the engine's own `t0` — the moment
   *  unity is asserted on it in `didAdd`. -1 until that callback has run.
   *
   *  IT IS HERE TO MAKE ONE QUESTION ANSWERABLE BY THE INSTRUMENT RATHER THAN BY
   *  EYE: did the assertion land before any sample was observable? Compare this
   *  against the first row that carries a readable `gain`. Sitting on the row is
   *  what lets `_r8` read it; a probe field would have meant touching 318 pinned
   *  assertions to answer a question the spool already answers.
   *
   *  CARRIED BY THE ROWS THAT REPORT A GAIN, not by command rows. A duck, resume
   *  or mute row has no `samples` beside it, so it is not the comparison point,
   *  and stamping a per-peer constant onto every row would be redundancy a reader
   *  has to see past. Optional here for the same reason `engine` is: this module
   *  reaches no engine and is handed everything it reports. */
  trackMs?: number;
}

/** UNITY, WITH THE DEADBAND THE GAIN IS ACTUALLY APPLIED UNDER. libwebrtc's
 *  channel_receive.cc stops applying the gain inside 0.99–1.01, and `rampGain`
 *  lands exactly on its target for that reason. Anything at or above this is
 *  audible; below it is not, and the audit counts it as held down. */
export const UNITY_FLOOR = 0.99;

/** A CHANGE, at the granularity the engine itself treats as "already there"
 *  (`rampGain`'s 0.005 early return). Below this there is nothing to report. */
export const TRANSITION_EPS = 0.005;

/** NO NEW TIMER — the periodic rows ride `playoutWitness`, which has run at 10Hz
 *  since 3b. These are TICK COUNTS on that existing cadence, nothing more.
 *
 * The first ten seconds below unity report once a second, because that is the
 * window where a duck, a refused duck and a lost resume are still separable by
 * their timing. After that the question is only "how long", so it backs off to
 * once every five seconds. THE BACK-OFF IS DECLARED, not silent: `seq` still
 * increments per row and `belowUnityMs` is absolute, so a reader can never
 * mistake decimation for the gain having recovered. */
export const PERIODIC_FAST_TICKS = 10;
export const PERIODIC_SLOW_TICKS = 50;
export const PERIODIC_FAST_ROWS = 10;

interface AuditState {
  seq: number;
  lastGain: number | null;
  lastSamples: number | null;
  belowUnitySince: number | null;
  ticksBelow: number;
  periodicRows: number;
  cause: GainCause;
  causeAt: number | null;
  delivered: GainDelivery;
  turnKey: string;
  undelivered: number;
}

function blank(): AuditState {
  return {
    seq: 0,
    lastGain: null,
    lastSamples: null,
    belowUnitySince: null,
    ticksBelow: 0,
    periodicRows: 0,
    cause: 'none',
    causeAt: null,
    delivered: 'unknown',
    turnKey: '',
    undelivered: 0,
  };
}

let s: AuditState = blank();

/** A NEW CALL INHERITS NOTHING. A duck belongs to a peer that no longer exists,
 *  and carrying its cause forward would attribute a fresh silence to a dead
 *  turn — the same class of error `duckedTurn = nil` prevents in Swift. */
export function gainAuditReset(): void {
  s = blank();
}

function row(
  kind: GainRowKind,
  gain: number,
  gainDelta: number,
  samples: number,
  samplesDelta: number,
  now: number,
  detail: string,
): GainAuditRow {
  s.seq += 1;
  // THE COUNT IS REPORTED HERE AND CLEARED SOMEWHERE ELSE (Sherman, build 31).
  //
  // This used to clear `s.undelivered` right here, on the reasoning that the row
  // now carries it. But composing a row is not sending one — the caller may drop
  // this very row — so three lost rows reported `1`, and the "once per gap"
  // transport report fired once per LOST ROW because the counter was back at zero
  // every time. A field that under-reports a gap is how the next round gets
  // misled, which is the failure this whole instrument exists to end.
  //
  // Cleared by `gainAuditDelivered` once a row has actually left. A row that is
  // dropped leaves the count standing, so it accumulates across the whole gap.
  return {
    seq: s.seq,
    deviceMs: now,
    kind,
    cause: s.cause,
    delivered: s.delivered,
    turnKey: s.turnKey,
    gain,
    gainDelta,
    samples,
    samplesDelta,
    sinceCommandMs: s.causeAt == null ? -1 : Math.max(0, now - s.causeAt),
    belowUnityMs: s.belowUnitySince == null ? 0 : Math.max(0, now - s.belowUnitySince),
    undelivered: s.undelivered,
    detail,
  };
}

/** A GAIN COMMAND WAS ISSUED — or a mute state arrived from the box.
 *
 *  Called AFTER the command itself has been issued, never before: the witness
 *  must not sit between a barge and the drop it commands.
 *
 *  `kind`:
 *   · `command` — issued here, outcome not yet known (`delivered: 'unknown'`).
 *   · `settled` — the same command's promise resolved or rejected. A `command`
 *     row with no `settled` twin is a command that never came back, which is the
 *     third possibility this round exists to make visible.
 *   · `echo`    — the box stated the mute state. The lock screen's mute never
 *     passes through JavaScript at all, so without this it would be invisible on
 *     the very call where the owner toggled six times. */
export function gainAuditCommand(
  cause: Exclude<GainCause, 'none'>,
  o: { kind?: GainRowKind; delivered?: GainDelivery; turnKey?: string; detail?: string; now?: number },
): GainAuditRow {
  const now = o.now ?? Date.now();
  const kind = o.kind ?? 'command';
  s.cause = cause;
  // A SETTLE DOES NOT RESTART THE CLOCK. `sinceCommandMs` has to mean "since the
  // barge fired", so only the ISSUE stamps it. A bridge call that comes back
  // thirty seconds late would otherwise reset the age of the very duck being
  // investigated, and every row after it would understate the silence.
  if (kind !== 'settled') s.causeAt = now;
  s.delivered = o.delivered ?? 'unknown';
  if (o.turnKey) s.turnKey = o.turnKey;
  return row(
    kind,
    s.lastGain == null ? -1 : s.lastGain,
    0,
    s.lastSamples == null ? -1 : s.lastSamples,
    0,
    now,
    o.detail ?? '',
  );
}

/** A SAMPLE FROM `playoutWitness`, at the engine's existing 10Hz.
 *
 *  Returns a row on a TRANSITION or on a decimated PERIODIC tick while the gain
 *  is held below unity, and `null` otherwise — which is the common case, and the
 *  reason this does not put ten rows a second on the wire.
 *
 *  A gain of -1 (NO REMOTE TRACK) is reported as a transition when it changes,
 *  and is never counted as "below unity": a missing track and a silenced one are
 *  different findings and the audit must not merge them. */
export function gainAuditSample(
  gain: number,
  samples: number,
  now?: number,
): GainAuditRow | null {
  const at = now ?? Date.now();
  const prevGain = s.lastGain;
  const prevSamples = s.lastSamples;
  const gainDelta = prevGain == null ? 0 : gain - prevGain;
  const samplesDelta = prevSamples == null ? 0 : samples - prevSamples;
  s.lastGain = gain;
  s.lastSamples = samples;

  const heldDown = gain >= 0 && gain < UNITY_FLOOR;
  if (heldDown) {
    if (s.belowUnitySince == null) {
      s.belowUnitySince = at;
      s.ticksBelow = 0;
      s.periodicRows = 0;
    }
  } else {
    s.belowUnitySince = null;
    s.ticksBelow = 0;
    s.periodicRows = 0;
  }

  if (prevGain != null && Math.abs(gainDelta) >= TRANSITION_EPS) {
    return row('transition', gain, gainDelta, samples, samplesDelta, at, '');
  }
  if (!heldDown) return null;

  s.ticksBelow += 1;
  const every = s.periodicRows < PERIODIC_FAST_ROWS ? PERIODIC_FAST_TICKS : PERIODIC_SLOW_TICKS;
  if (s.ticksBelow < every) return null;
  s.ticksBelow = 0;
  s.periodicRows += 1;
  return row('periodic', gain, gainDelta, samples, samplesDelta, at, '');
}

/** A ROW THAT NEVER LEFT. Counted, never retried: the next row that lands
 *  carries the count, so the gap is stated by the trail itself.
 *
 *  Returns the running total. Because nothing clears it until a row actually
 *  leaves, a return of exactly 1 is the START OF A GAP and nothing else — which
 *  is what makes "report once per gap" true at the caller instead of a claim. */
export function gainAuditUndelivered(): number {
  s.undelivered += 1;
  return s.undelivered;
}

/** A ROW LANDED, CARRYING `count` LOST ONES WITH IT. Only now is the debt paid.
 *
 *  Clamped at zero and subtractive rather than reset-to-zero: a row composed
 *  before a further loss must not clear a gap it never reported. */
export function gainAuditDelivered(count: number): void {
  if (count > 0) s.undelivered = Math.max(0, s.undelivered - count);
}
